import { Menu, Overlay, ScrollArea } from '@mantine/core';
import { RecordTypeIndicator } from './RecordTypeIndicator';
import type { Coordinate } from '../browser/implementations/WebRtcVmView';
import { capFirst } from '~/common/string/capitalize';
import { camelCaseToWords } from '~/common/string/camelCase';
import {
  IconClick,
  IconClock,
  IconCommand,
  IconDragDrop,
  IconEye,
  IconForms,
  IconPointer,
  IconTerminal2,
  IconSparkles,
  IconSwipe,
  IconArrowsDownUp,
  IconMouse2,
  IconKeyboard,
  IconBackspace,
} from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { recordTypeAtom } from '~/atoms/recorder-atoms';
import type { Dispatch, SetStateAction } from 'react';
import React, { useMemo, useState } from 'react';
import { useTimeout } from '@mantine/hooks';
import type { ValueType } from '~/server/logic/steps/operation/types';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import type { TestType } from '@prisma/client';
import type { VmFillInputPayload } from '~/common/vm/protocol';

export type RecorderOperation = {
  method:
    | 'dragTo'
    | 'click'
    | 'dblclick'
    | 'fill'
    | 'clear'
    | 'type'
    | 'goto'
    | 'hover'
    | 'selectOption'
    | 'toBeVisible'
    | 'toContainText'
    // TODO: deprecate toHaveScreenshot
    | 'toHaveScreenshot'
    | 'press'
    | 'waitForTimeout'
    | 'aiAssertion'
    | 'aiAction'
    | 'playwrightAction'
    | 'swipe'
    | 'scroll';
  hasValue?: boolean;
  title?: string;
  icon?: React.ReactNode;
  level?: 'element' | 'page';
  type?: 'WEB' | 'MOBILE' | 'BOTH';
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  options?: Record<string, any>;
};

const ASSERTION_CHOICES: RecorderOperation[] = [
  { method: 'toBeVisible', icon: <IconEye />, level: 'element', type: 'WEB' },
  // Disabled because it doesn't work most of the time. But keep the related code for existing operations
  // { method: 'toContainText', icon: <IconForms />, hasValue: true, level: 'element', type: 'WEB' },
  {
    method: 'aiAssertion',
    title: 'AI Assert',
    icon: <IconSparkles />,
    level: 'page',
    hasValue: true,
    type: 'BOTH',
  },
];

const ACTION_CHOICES: RecorderOperation[] = [
  { method: 'fill', icon: <IconForms />, hasValue: true, level: 'element', type: 'BOTH' },
  { method: 'clear', icon: <IconBackspace />, level: 'element', type: 'BOTH' },
  { method: 'dblclick', title: 'Double Click', icon: <IconClick />, level: 'element', type: 'WEB' },
  {
    method: 'click',
    title: 'Right Click',
    icon: <IconMouse2 />,
    level: 'element',
    type: 'WEB',
    options: { button: 'right' },
  },
  { method: 'hover', icon: <IconPointer />, level: 'element', type: 'WEB' },
  { method: 'dragTo', icon: <IconDragDrop />, hasValue: true, level: 'element', type: 'WEB' },
  { method: 'scroll', title: 'Scroll', icon: <IconArrowsDownUp />, hasValue: true, level: 'element', type: 'WEB' },
  { method: 'press', icon: <IconCommand />, hasValue: true, level: 'page', type: 'WEB' },
  { method: 'type', icon: <IconKeyboard />, hasValue: true, level: 'page', type: 'WEB' },
  { method: 'swipe', title: 'Swipe', icon: <IconSwipe />, hasValue: true, level: 'element', type: 'MOBILE' },
  { method: 'waitForTimeout', title: 'Wait', icon: <IconClock />, hasValue: true, level: 'page', type: 'BOTH' },
  { method: 'aiAction', title: 'AI Generate', icon: <IconSparkles />, hasValue: true, level: 'page', type: 'BOTH' },
];

const ADMIN_ICON_CLASSNAME = 'text-orange-600 hover:bg-orange-500/10 dark:text-orange-400';
const ACTION_CHOICES_ADMIN: RecorderOperation[] = [
  {
    method: 'playwrightAction',
    title: 'Playwright Action',
    icon: <IconTerminal2 className={ADMIN_ICON_CLASSNAME} />,
    hasValue: true,
    level: 'page',
    type: 'WEB',
  },
];

const camelCaseToCapWords = (inp: string) => {
  return camelCaseToWords(inp)
    .toLowerCase()
    .split(' ')
    .map((word) => capFirst(word))
    .join(' ');
};

const OperationChoiceMenu = ({
  choices,
  recorderFunctions: { valueEntryOpen, setAction, saveAction, resetFillInputData },
}: {
  choices: RecorderOperation[];
  recorderFunctions: {
    valueEntryOpen: () => void;
    setAction: Dispatch<SetStateAction<RecorderOperation | undefined>>;
    saveAction: (action?: RecorderOperation, actionValue?: { value: string; valueType?: ValueType }) => void;
    // Must reset fill input data, otherwise the input type check will be wrong
    // Ideally we should get the new input data (that includes the input type such as 'password', 'email', etc.).
    // But it's too complicated so we just clear it for now.
    resetFillInputData: () => void;
  };
}) => {
  return (
    <>
      {choices.map((opChoice, idx, arr) => {
        // Can be undefined at negativer index
        const prevOpChoice: RecorderOperation | undefined = arr[idx - 1];
        return (
          <div key={`${opChoice.level}-${opChoice.method}`}>
            {prevOpChoice && prevOpChoice.level !== opChoice.level ?
              <Menu.Label className="first-letter:capitalize">{opChoice.level}</Menu.Label>
            : null}
            <Menu.Item
              key={opChoice.method}
              leftSection={opChoice.icon}
              className="first-letter:capitalize"
              onClick={async () => {
                if (opChoice.method === 'fill') {
                  resetFillInputData();
                }
                setAction(opChoice);
                if (opChoice.hasValue) {
                  valueEntryOpen();
                } else {
                  saveAction(opChoice);
                }
              }}
            >
              {opChoice.title || camelCaseToCapWords(opChoice.method)}
            </Menu.Item>
          </div>
        );
      })}
    </>
  );
};

const BrowserContextMenu = ({
  zIndex,
  browserClickPosition,
  valueEntryOpen,
  setMainDropdown,
  setFillInputData,
  setAction,
  saveAction,
  testType,
}: {
  zIndex: number;
  browserClickPosition: { x: Coordinate; y: Coordinate };
  valueEntryOpen: () => void;
  setMainDropdown: Dispatch<SetStateAction<HTMLDivElement | null>>;
  setFillInputData: Dispatch<SetStateAction<VmFillInputPayload['data'] | undefined>>;
  setAction: Dispatch<SetStateAction<RecorderOperation | undefined>>;
  saveAction: (action?: RecorderOperation, actionValue?: { value: string; valueType?: ValueType }) => void;
  testType: TestType;
}) => {
  const { isAdmin } = useAuthData();
  // Mobile web and web are both considered 'WEB' as functionality between the two is the same
  const testTypeMapped = testType === 'MOBILE_APP' ? 'MOBILE' : 'WEB';

  const actionChoices: RecorderOperation[] = useMemo(
    () =>
      [...ACTION_CHOICES, ...(isAdmin ? ACTION_CHOICES_ADMIN : [])].sort((a, b) =>
        a.level === 'element' && b.level === 'page' ? -1 : 0,
      ),
    [isAdmin],
  );

  const recordType = useAtomValue(recordTypeAtom);
  const [isVisible, setVisible] = useState(false); // needed for mantinue opacity change
  useTimeout(() => setVisible(true), 10, { autoInvoke: true });

  const recorderFunctions = useMemo(
    () => ({
      valueEntryOpen,
      setAction,
      saveAction,
      resetFillInputData: () => setFillInputData(undefined),
    }),
    [valueEntryOpen, setAction, saveAction, setFillInputData],
  );

  return (
    <div>
      <Overlay
        zIndex={zIndex}
        className="pointer-events-none opacity-50"
      />
      {/* We have to do this div position nesting for the mantine dialog to show up properly. */}
      {/* Just messing with the target does not work (weird refresh issues) */}
      <div
        style={{
          position: 'absolute',
          left: browserClickPosition.x.absolute,
          top: browserClickPosition.y.absolute,
        }}
        className="-translate-x-1 -translate-y-1"
        role="menu"
      >
        <Menu
          opened
          shadow="md"
          zIndex={zIndex + 1}
          // If not within portal, it can get hidden and causes weird scrolling things to occur
          withinPortal
          // Needed for position to update on second click
          positionDependencies={[browserClickPosition]}
          withArrow
        >
          <Menu.Target>
            <div className="h-2 w-2 rounded-full border-2 border-[#334155] bg-slate-200" />
          </Menu.Target>

          <Menu.Dropdown
            ref={setMainDropdown}
            className="min-w-48 transition-opacity ease-in"
            opacity={isVisible ? 1 : 0}
          >
            <RecordTypeIndicator className="mb-1" />
            <ScrollArea.Autosize
              className="overflow-y-auto"
              // Use similar constraints as AddOperationMenu so the dropdown never exceeds viewport
              mah="min(600px, calc(40vh))"
              type="always"
              scrollbars="y"
              scrollbarSize={8}
            >
              {recordType === 'action' ?
                <OperationChoiceMenu
                  choices={actionChoices.filter((op) => op.type === 'BOTH' || op.type === testTypeMapped)}
                  recorderFunctions={recorderFunctions}
                />
              : recordType === 'assertion' ?
                <OperationChoiceMenu
                  choices={ASSERTION_CHOICES.filter((op) => op.type === 'BOTH' || op.type === testTypeMapped)}
                  recorderFunctions={recorderFunctions}
                />
              : null}
            </ScrollArea.Autosize>
          </Menu.Dropdown>
        </Menu>
      </div>
    </div>
  );
};

export default BrowserContextMenu;
